import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { CTASection } from "../components/CTASection";
import { SERVICES } from "../data/site";

export default function Services() {
  return (
    <div data-testid="services-page">
      <Seo
        title="Managed IT, Cybersecurity & Cloud Services in Atlanta | Zentryx Systems"
        description="Managed IT services, cybersecurity, Microsoft 365 support, cloud, backup & disaster recovery, network support, and IT consulting for Atlanta small and midsize businesses."
        path="/services"
      />
      <section className="pt-32 sm:pt-40 pb-16 px-5 sm:px-8 bg-mist">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">Services</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-charcoal leading-tight">
              Managed IT &amp; cybersecurity for Atlanta businesses.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              From responsive day-to-day support to long-term strategy, Zentryx covers every layer of your technology — practical, secure, and right-sized for small and midsize firms.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 2) * 0.08}>
              <div id={s.slug} className="scroll-mt-28 rounded-3xl border border-slate-200 bg-white p-8 sm:p-10 grid lg:grid-cols-3 gap-8 hover:shadow-lg hover:shadow-slate-200/60 transition-shadow">
                <div className="lg:col-span-1">
                  <div className="h-12 w-12 rounded-2xl bg-teal/10 flex items-center justify-center mb-5">
                    <s.icon className="h-6 w-6 text-teal" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-charcoal">{s.title}</h2>
                  <p className="mt-3 text-slate-600 leading-relaxed">{s.short}</p>
                  <p className="mt-4 text-sm text-teal font-medium leading-relaxed">{s.outcome}</p>
                </div>
                <div className="lg:col-span-2 grid sm:grid-cols-2 gap-x-8 gap-y-4 content-center">
                  {s.points.map((p) => (
                    <div key={p} className="flex items-start gap-3">
                      <span className="mt-0.5 h-5 w-5 rounded-full bg-emerald/10 flex items-center justify-center flex-shrink-0">
                        <Check className="h-3 w-3 text-emerald" />
                      </span>
                      <span className="text-sm text-charcoal">{p}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* INDUSTRY LINKS */}
        <div className="max-w-7xl mx-auto mt-16">
          <h2 className="font-heading text-2xl font-bold text-charcoal mb-6">Industries we serve</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { to: "/industries/cpa-accounting-firms-atlanta", t: "IT for CPA & Accounting Firms", d: "Secure, reliable IT built for busy season and sensitive client data." },
              { to: "/industries/law-firms", t: "IT for Law Firms", d: "Confidentiality-first IT and cybersecurity for Atlanta legal practices." },
              { to: "/industries/healthcare-practices", t: "IT for Healthcare Practices", d: "HIPAA-aware IT that protects patient data and keeps systems running." },
              { to: "/industries/architecture-engineering-firms-atlanta", t: "IT for Architecture & Engineering", d: "Performance, uptime, and secure collaboration for design teams." },
              { to: "/industries/professional-services", t: "IT for Professional Services", d: "Scalable IT and security for agencies, consultants, and advisors." },
              { to: "/industries/financial-services", t: "IT for Financial Services", d: "Security-first IT and continuity for advisors and finance firms." },
            ].map((ind) => (
              <Link key={ind.to} to={ind.to}
                className="group rounded-3xl bg-mist border border-slate-200 p-8 hover:shadow-lg hover:shadow-slate-200/60 transition-all">
                <h3 className="font-heading text-xl font-bold text-charcoal">{ind.t}</h3>
                <p className="mt-2 text-sm text-slate-600">{ind.d}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-navy group-hover:gap-2.5 transition-all">See how we help <ArrowRight className="h-3.5 w-3.5" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CTASection />
    </div>
  );
}
